import { useCallback, useEffect, useRef, useState } from "react";
import { AI, USER } from "@root/constants";

interface UseChatScrollOptions {
  messages: any[];
  isPolling: boolean;
  scrollThreshold?: number;
}

interface UseChatScrollReturn {
  chatMessagesRef: React.RefObject<HTMLDivElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  showScrollToBottomButton: boolean;
  handleScrollToBottomClick: () => void;
}

export const useChatScroll = ({
  messages,
  isPolling,
  scrollThreshold = 100,
}: UseChatScrollOptions): UseChatScrollReturn => {
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isUserScrolledUpRef = useRef(false);
  const previousMessageCountRef = useRef(0);
  const isUserScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mutationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);
  const [showScrollToBottomButton, setShowScrollToBottomButton] =
    useState(false);

  // Helper function to check if user is near bottom
  const isNearBottom = useCallback(
    (container: HTMLElement): boolean => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      return distanceFromBottom <= scrollThreshold;
    },
    [scrollThreshold]
  );

  // Main scroll effect with auto-scroll logic
  useEffect(() => {
    if (!chatMessagesRef?.current || !messagesEndRef?.current) return;

    const container = chatMessagesRef?.current;

    const scrollToBottom = (force = false) => {
      // Don't auto-scroll if user is actively scrolling (unless forced)
      if (!force && isUserScrollingRef.current) {
        return;
      }
      // Only scroll if user is near bottom or hasn't manually scrolled up (or if forced)
      if (force || !isUserScrolledUpRef?.current || isNearBottom(container)) {
        messagesEndRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    };

    // Initialize scroll position tracking
    lastScrollTopRef.current = container.scrollTop;

    // Handle scroll events to detect user scrolling up
    const handleScroll = () => {
      const currentScrollTop = container?.scrollTop;
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTopRef.current);
      lastScrollTopRef.current = currentScrollTop;

      // If scroll delta is very small, it might be a programmatic scroll
      // Only treat as user scroll if delta is significant (> 5px)
      if (scrollDelta > 5 && !isProgrammaticScrollRef?.current) {
        // Mark that user is actively scrolling
        isUserScrollingRef.current = true;

        // Clear existing timeout
        if (scrollTimeoutRef?.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        // Reset the scrolling flag after user stops scrolling
        scrollTimeoutRef.current = setTimeout(() => {
          isUserScrollingRef.current = false;
        }, 100);
      } else {
        // Small scroll delta, likely programmatic - don't block auto-scroll
        isProgrammaticScrollRef.current = false;
      }

      const nearBottom = isNearBottom(container);
      isUserScrolledUpRef.current = !nearBottom;
      // Show scroll button if user is scrolled up and there's new content
      const hasNewContent = isPolling || (messages && messages.length > 0);
      setShowScrollToBottomButton(!nearBottom && hasNewContent);
    };

    // Immediate scroll on mount or new message (only if near bottom)
    const immediateScroll = setTimeout(() => {
      const nearBottom = isNearBottom(container);
      // Always scroll if near bottom, or if user hasn't scrolled up
      if (nearBottom || !isUserScrolledUpRef?.current) {
        isProgrammaticScrollRef.current = true;
        scrollToBottom(true); // Force scroll for new messages
      }
    }, 0);

    // Watch for content changes (AI generating text)
    // Use a debounced approach to avoid too frequent calls
    const observer = new MutationObserver(() => {
      // Debounce mutation observer calls to reduce interference
      if (mutationTimeoutRef?.current) {
        clearTimeout(mutationTimeoutRef.current);
      }
      mutationTimeoutRef.current = setTimeout(() => {
        const nearBottom = isNearBottom(container);
        // Auto-scroll if user is near bottom or hasn't scrolled up
        // Only skip if user is actively scrolling and scrolled up
        if (
          !isUserScrollingRef?.current &&
          (!isUserScrolledUpRef?.current || nearBottom)
        ) {
          isProgrammaticScrollRef.current = true;
          scrollToBottom();
        }
        // Update button visibility when content changes
        const hasNewContent = isPolling || (messages && messages.length > 0);
        setShowScrollToBottomButton(!nearBottom && hasNewContent);
      }, 50);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Add scroll event listener
    container.addEventListener("scroll", handleScroll, { passive: true });

    // Continuous scroll during AI generation (only if near bottom)
    // Use requestAnimationFrame for smoother, less intrusive scrolling
    let animationFrameId: number | null = null;
    let lastScrollTime = 0;

    const performAutoScroll = () => {
      if (!isPolling) {
        return;
      }

      const now = Date.now();
      // Only check every 100ms to reduce interference
      if (now - lastScrollTime >= 100) {
        lastScrollTime = now;
        const nearBottom = isNearBottom(container);
        // Only auto-scroll if near bottom or user hasn't scrolled up
        if (!isUserScrolledUpRef?.current || nearBottom) {
          // Only scroll if not actively scrolling, or if we're very close to bottom
          if (!isUserScrollingRef?.current || nearBottom) {
            isProgrammaticScrollRef.current = true;
            scrollToBottom();
          }
        }
      }

      animationFrameId = requestAnimationFrame(performAutoScroll);
    };

    if (isPolling) {
      animationFrameId = requestAnimationFrame(performAutoScroll);
    }

    return () => {
      clearTimeout(immediateScroll);
      observer.disconnect();
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef?.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (mutationTimeoutRef?.current) {
        clearTimeout(mutationTimeoutRef.current);
      }
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [messages, isPolling, isNearBottom]);

  // Reset scroll state and scroll to bottom when new chat starts or new message is added
  useEffect(() => {
    const currentMessageCount = messages?.length || 0;
    const previousMessageCount = previousMessageCountRef.current;

    // Reset when messages array is empty (new chat)
    if (currentMessageCount === 0) {
      isUserScrolledUpRef.current = false;
      previousMessageCountRef.current = 0;
      // Scroll to bottom for new chat
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
      return;
    }

    // Check if a new message was added (count increased)
    if (currentMessageCount > previousMessageCount) {
      const lastMessage = messages[currentMessageCount - 1];

      // When a new user message is added, always reset the scroll state and scroll to bottom
      // This ensures the user sees their new query and the response
      if (lastMessage?.type === USER) {
        isUserScrolledUpRef.current = false;
        isUserScrollingRef.current = false; // Reset scrolling flag
        setShowScrollToBottomButton(false);
        // Force scroll to bottom when user sends a new query
        setTimeout(() => {
          if (messagesEndRef.current && chatMessagesRef.current) {
            // Mark as programmatic scroll
            isProgrammaticScrollRef.current = true;
            messagesEndRef.current.scrollIntoView({
              behavior: "smooth",
              block: "end",
            });
            // Update scroll position tracking
            setTimeout(() => {
              if (chatMessagesRef.current) {
                lastScrollTopRef.current = chatMessagesRef.current.scrollTop;
              }
            }, 50);
          }
        }, 0);
      } else if (lastMessage?.type === AI && chatMessagesRef.current) {
        // When new AI message is added, check if button should be shown
        const container = chatMessagesRef.current;
        const nearBottom = isNearBottom(container);
        if (!nearBottom) {
          setShowScrollToBottomButton(true);
        } else {
          // If near bottom, ensure we auto-scroll
          isUserScrolledUpRef.current = false;
        }
      }
    }

    // Update the previous message count
    previousMessageCountRef.current = currentMessageCount;
  }, [messages, isNearBottom]);

  // Reset scroll state when polling starts (new AI response beginning)
  // Only reset if user is near bottom to respect manual scrolling
  useEffect(() => {
    if (isPolling && chatMessagesRef.current) {
      const container = chatMessagesRef.current;
      // If user is near bottom when new response starts, enable auto-scroll
      if (isNearBottom(container)) {
        isUserScrolledUpRef.current = false;
        setShowScrollToBottomButton(false);
      } else {
        // Show scroll button if user is scrolled up during polling
        setShowScrollToBottomButton(true);
      }
    } else if (!isPolling && chatMessagesRef.current) {
      // Hide button when polling stops and user is near bottom
      const container = chatMessagesRef.current;
      if (isNearBottom(container)) {
        setShowScrollToBottomButton(false);
      }
    }
  }, [isPolling, isNearBottom]);

  // Handle scroll to bottom button click
  const handleScrollToBottomClick = () => {
    if (messagesEndRef.current) {
      isUserScrolledUpRef.current = false;
      setShowScrollToBottomButton(false);
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  };

  return {
    chatMessagesRef,
    messagesEndRef,
    showScrollToBottomButton,
    handleScrollToBottomClick,
  };
};
